import { useState } from 'react';
import {
  Autocomplete,
  DropDown,
  DropDownOption,
  FlexColumn,
  TextField,
  FormControls,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { isValidFileName } from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddTestForm, Resource } from 'shared/types';
import QualityDimensionSelector from '../QualityDimensionSelector';
import SeveritySelector from '../SeveritySelector';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  singularTestPaths: string[];
  tableResourcesData: Resource[];
};

const CustomQuery = ({
  methods,
  singularTestPaths,
  tableResourcesData,
}: Props) => {
  const [expanded, setExpanded] = useState(true);

  const testedTableOptions: DropDownOption[] = tableResourcesData.map(
    (table) => ({
      value: table.id,
      label: table.source_name
        ? `${table.source_name}.${table.name}`
        : table.name,
    }),
  );

  return (
    <FormControls.TestAccordion
      summary="Custom query"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: 'bold' }}>
            Test configuration
          </Typography>
          <FormControls.Row>
            <FormControls.FormText>Test name:</FormControls.FormText>
            <FormControls.InputWrapper>
              <TextField
                size="large"
                error={!!methods.formState?.errors?.testName}
                {...methods.register('testName', {
                  validate: (value) =>
                    !value ||
                    isValidFileName(value) ||
                    'Please use only letters, numbers, and underscores',
                })}
              />
              <FormControls.ErrorMessage>
                {methods.formState?.errors?.testName?.message}
              </FormControls.ErrorMessage>
            </FormControls.InputWrapper>
            <InfoTooltip title="This will be the name of your test. Please use only letters, numbers, and underscores" />
          </FormControls.Row>

          <TableTestDescriptionField methods={methods} />

          <Controller
            name="severity"
            control={methods.control}
            render={({ field }) => <SeveritySelector {...field} />}
            defaultValue={{ type: 'error' }}
          />
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <FormControls.FieldsWrapper>
          <FormControls.TestAccordion
            variant="secondary"
            summary={
              <Typography sx={{ fontWeight: 'bold' }}>
                Advanced configuration
              </Typography>
            }
          >
            <FlexColumn sx={{ pt: '18px', gap: '24px' }}>
              <FormControls.Row>
                <FormControls.FormText>Location:</FormControls.FormText>
                <Controller
                  name="path"
                  control={methods.control}
                  defaultValue="tests"
                  render={({ field }) => (
                    <Autocomplete
                      sx={{ width: '100%' }}
                      size="large"
                      options={singularTestPaths}
                      freeSolo
                      onSelect={field.onChange}
                      {...field}
                      onChange={undefined}
                    />
                  )}
                />
              </FormControls.Row>

              <FormControls.Row>
                <FormControls.FormText>
                  Quality dimension:
                </FormControls.FormText>
                <Controller
                  name="qualityDimension"
                  control={methods.control}
                  render={({ field }) => (
                    <QualityDimensionSelector
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </FormControls.Row>
              <FormControls.Row>
                <FormControls.FormText>Tested table:</FormControls.FormText>
                <Controller
                  name="testedTable"
                  control={methods.control}
                  render={({ field }) => (
                    <DropDown
                      size="large"
                      placeholder="Choose tested table"
                      options={testedTableOptions}
                      showSearch
                      showClear
                      {...field}
                    />
                  )}
                />
                <InfoTooltip title="This will determine which table the test will be associated with, you will see the test results paired with the selected table in the side bar and lineage" />
              </FormControls.Row>
            </FlexColumn>
          </FormControls.TestAccordion>
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default CustomQuery;
